import React, { useState } from 'react';

import Environments, { APIs } from '@delta-defense/client-environments';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { API } from '../../api/API';
import { isUserUpdateResponseBodySuccess } from '../../api/users/guards';
import { isVerifyCodeResponseBodySuccess } from '../../api/verifications/guards';
import { VerifyCodeRequestBody, VerifyCodeResponseBodySuccess } from '../../api/verifications/types';
import { passwordRegex } from '../../constants';
import { CookieService } from '../../core/services/cookieService';
import AuthButton from '../AuthButton/AuthButton';
import PasswordResetSuccess from '../CompletedForms/PasswordResetSuccess';
import { DefaultErrorPage } from '../ErrorPage/defaultErrorPage';
import { InputGroup } from '../InputGroup/InputGroup';
import Loading from '../Loading/Loading';
import Title from '../Title/Title';
import './CreateNewPassword.scss';
import messages from './messages';

export type CreateNewPasswordParams = {
  verificationId: string;
}

type CreateNewPasswordProps = {
  cookieService: ReturnType<typeof CookieService.Instance>
} & RouteComponentProps<CreateNewPasswordParams>;

export function CreateNewPassword(props: CreateNewPasswordProps): React.JSX.Element {
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [authorizationError, setAuthorizationError] = useState<string | null>(null);
  const [forgotPasswordRedirect, setForgotPasswordRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordResetSuccessful, setIsPasswordResetSuccessful] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [userUpdateError, setUserUpdateError] = useState<number | null>(null);

  async function onSubmitHandler(submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> {
    submitEvent.preventDefault();
    setIsLoading(true);

    if (!passwordRegex.test(password)) {
      setPasswordMessage(messages.passwordRequirementsErrorMessage);
      return;
    }

    if (password !== passwordConfirmation) {
      setPasswordError(messages.passwordConfirmationError);
      return;
    }

    if (!validateAuthTokenFormat(authorizationCode)) {
      setAuthorizationError(messages.authorizationCodeFormat);
      return;
    }

    const authTokenResponse = await postAuthToken(authorizationCode);

    const expiredAuthResponseCodes = new Set([404, 410, 429]);
    if (expiredAuthResponseCodes.has(authTokenResponse.status)) {
      setForgotPasswordRedirect(true);
      return;
    }

    const authTokenSuccessBody = await API.validateResponseJSON(authTokenResponse, isVerifyCodeResponseBodySuccess);
    if (!authTokenSuccessBody) {
      setAuthorizationError(messages.verificationErrorMessage);
      return;
    }

    const { attributes: { token, ttl } } = authTokenSuccessBody.data;
    const userId = props.cookieService.decodeToken(token)?.userId || ''; // FIXME: add types to decodeToken
    const passwordResponse = await patchNewPassword(userId, token);
    const passwordResponseBody = await API.validateResponseJSON(passwordResponse, isUserUpdateResponseBodySuccess);

    if (passwordResponseBody) {
      await props.cookieService.setAccessToken(token, ttl);
      setIsPasswordResetSuccessful(true);
    } else {
      setUserUpdateError(passwordResponse.status);
    }
  }

  function validateAuthTokenFormat(code: string): boolean {
    const codePattern = /\d{6}/;
    const codeLength = 6;

    return !!(code.match(codePattern) && code.length === codeLength);
  }

  async function postAuthToken(token: string): Promise<Response> {
    const verificationId = props.match.params.verificationId;
    const endpointURL = `${Environments.getProxyEndpointForApi(APIs.Auth)}/verifications/${verificationId}/tokens`;
    const postBody: VerifyCodeRequestBody = {
      code: token
    };

    const response = await API.post(endpointURL, postBody);

    return response;
  }

  async function patchNewPassword(
    id: VerifyCodeResponseBodySuccess['data']['id'],
    token: VerifyCodeResponseBodySuccess['data']['attributes']['token']
  ): Promise<Response> {
    const endpointURL = `${Environments.getProxyEndpointForApi(APIs.Auth)}/users/${id}`;
    const patchBody = { password };

    const response = await API.patch(endpointURL, patchBody, {
      headers: {
        'X-Auth-Token': token
      }
    });

    return response;
  }

  if (forgotPasswordRedirect) {
    return <Redirect push to={{ pathname: '/forgot-password' }} />;
  } else if (userUpdateError) {
    return <DefaultErrorPage errorStatus={userUpdateError} />;
  } else if (isPasswordResetSuccessful) {
    return (<PasswordResetSuccess />);
  } else {
    return (
      <>
        <Title name="Reset Password" />

        <p className='form-text'>
          For your security, we've sent an authorization code to your email.
          If you've listed a mobile phone number, we have sent the authorization code via text.
          Enter the code below and create a new password. <b>This code is only valid for 1 hour.</b>
        </p>

        <form
          name='create-new-password'
          onSubmit={e => onSubmitHandler(e).finally(() => setIsLoading(false))}
        >
          <InputGroup
            id='accountAuthorizationCode'
            name='authorizationCode'
            label='Authorization Code'
            type='text'
            value={authorizationCode}
            onChange={e => setAuthorizationCode(e.target.value)}
            autoComplete='one-time-code'
            inputMode='numeric'
            required
            error={authorizationError}
          />

          <InputGroup
            id='createAccountPassword'
            name='password'
            label='Password'
            type='password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete='new-password'
            required
            error={passwordError}
          />
          {passwordMessage && (
            <p className="mt-1 form-text">{passwordMessage}</p>
          )}

          <InputGroup
            id="createAccountReEnterPassword"
            name='passwordConfirmation'
            label='Password Confirmation'
            type='password'
            value={passwordConfirmation}
            onChange={e => setPasswordConfirmation(e.target.value)}
            autoComplete='new-password'
            required
          />

          <AuthButton
            id="resetPasswordSave"
            color="btn-gold"
            text={isLoading ? <Loading /> : 'Save Password'}
            disabled={isLoading}
          />
        </form>
      </>
    );
  }
}
